import { z } from 'zod';
import { CONTENT_TYPES, SCREEN_ORIENTATIONS, SCREEN_STATUSES } from '../constants.js';

/**
 * Floor plans: each location has a plan (optionally a picture of it) and its screens are placed
 * on it where they hang. Positions are fractions of the plan, so a plan looks the same at any
 * size and survives a new background picture of the same shape.
 */

export const planPositionSchema = z.object({
  /** Left edge, as a fraction of the plan's width. */
  x: z.number().min(0).max(1),
  /** Top edge, as a fraction of the plan's height. */
  y: z.number().min(0).max(1),
  /** Width of the screen's tile, as a fraction of the plan's width. */
  w: z.number().min(0.02).max(1),
});
export type PlanPosition = z.infer<typeof planPositionSchema>;

export const FLOOR_PLAN_DEFAULT_ASPECT = 16 / 10;

export const floorPlanScreenSchema = z.object({
  id: z.string(),
  name: z.string(),
  orientation: z.enum(SCREEN_ORIENTATIONS),
  width: z.number().int(),
  height: z.number().int(),
  status: z.enum(SCREEN_STATUSES),
  tags: z.array(z.string()),
  position: planPositionSchema.nullable(),
  screenshotUrl: z.string().nullable(),
  screenshotAt: z.string().nullable(),
  /** What the schedules say plays here right now; null when nothing is scheduled. */
  playing: z
    .object({
      scheduleId: z.string(),
      scheduleName: z.string(),
      contentType: z.enum(CONTENT_TYPES),
      contentId: z.string(),
      contentName: z.string().nullable(),
    })
    .nullable(),
});
export type FloorPlanScreen = z.infer<typeof floorPlanScreenSchema>;

export const floorPlanSchema = z.object({
  locationId: z.string(),
  locationName: z.string(),
  /** Width divided by height. */
  aspect: z.number(),
  background: z.object({ mediaId: z.string(), url: z.string().nullable() }).nullable(),
  /** The screens of this location (not of the locations under it). */
  screens: z.array(floorPlanScreenSchema),
});
export type FloorPlan = z.infer<typeof floorPlanSchema>;

export const saveFloorPlanSchema = z.object({
  backgroundMediaId: z.string().uuid().nullable().optional(),
  aspect: z.number().min(0.25).max(4).optional(),
  /** Only the screens listed change; `position: null` takes a screen off the plan. */
  positions: z
    .array(z.object({ screenId: z.string().uuid(), position: planPositionSchema.nullable() }))
    .max(500)
    .optional(),
});
export type SaveFloorPlanInput = z.infer<typeof saveFloorPlanSchema>;
